/**
 * First-party funnel beacon. Fires milestone events to the public
 * `POST /events/collect` endpoint, keyed by the anonymous `tenders_vid` (shared
 * with Meta `external_id`). Never blocks, never throws, no-ops on SSR.
 *
 * Uses `fetch` with `keepalive` (not `sendBeacon`): the beacon is cross-origin
 * JSON, and a `sendBeacon` Blob of type application/json triggers a CORS
 * preflight it can't perform, whereas `keepalive` fetch survives page unload AND
 * carries the JSON content-type the DTO expects.
 *
 * `credentials: 'include'` is what lets a signed-in visitor's milestones carry a
 * user id as well as the visitor id. This app has no bearer token in the
 * browser — nothing ever writes one to localStorage; the session is the httpOnly
 * `tenders_session` cookie, which the JWT strategy extracts (`jwt.strategy.ts`)
 * and which every other call sends the same way. With `'omit'` the endpoint saw
 * *every* event as anonymous, so no funnel could join a browser milestone to a
 * server one (`registered`, `checkout_started`, `paid`) except through
 * `visitor_links`.
 *
 * Costs no extra round-trip — the JSON content-type already forces a preflight,
 * and the API answers it with `allow-credentials: true` for this exact origin.
 * Opens no CSRF surface either: a cross-site page cannot make this request (CORS
 * allow-lists one origin), and `sameSite: 'lax'` withholds the cookie from a
 * cross-site form POST, which is the only shape that would dodge the preflight.
 */
import { apiBaseUrl } from './api';
import { deviceContext, type DeviceContext } from './device-context';
import { detectInAppBrowser, inAppContext, type InAppOs } from './in-app-browser';
import { getOrCreateVisitorId } from './meta-cookies';
import { phCapture } from './posthog';

const COLLECT_URL = `${apiBaseUrl}/events/collect`;

/**
 * Longest search query any system may retain.
 *
 * MUST match SEARCH_QUERY_MAX_LEN in
 * services/api/src/modules/events/events.controller.ts — a cross-package test
 * in funnel.spec.ts asserts the two are equal.
 *
 * It lives here as well as on the server because `track()` hands the event to
 * PostHog before the request leaves the browser, so a server-only cap never
 * reaches PostHog at all. The server keeps its copy as the backstop: this
 * beacon is public, so a client-side limit is a suggestion.
 */
export const SEARCH_QUERY_MAX_LEN = 80;

/**
 * What kind of browser this page is being read in — stamped on EVERY event.
 *
 * ## Why it is on every event and not only on the checkout ones
 *
 * Three mobile decisions had no denominator until 2026-09-26, each for the same
 * reason: the device keys existed only where somebody remembered to spread them.
 *   - 109 of the 132 buyers recorded as "mobile browser" were indistinguishable
 *     from Instagram/Telegram in-app visitors, because `inApp` was written only
 *     since 19.09 and only when it was TRUE — so "absent" meant both "normal
 *     browser" and "nobody looked".
 *   - The 20 Octobank hand-off rows carried no device at all.
 *   - Everything upstream of the sheet (`plans_viewed`, `paywall_shown`,
 *     `session_start`) carried none either, so a per-device funnel could not be
 *     built above the checkout.
 *
 * Hence the contract: **the four keys are always present**. `inApp: false` and
 * `inAppOs: null` mean MEASURED AND NOT IN-APP, which is a different fact from a
 * missing key, and the whole point of writing them.
 *
 * ⚠️ Series break at the deploy of this branch: rows written before it lack the
 * keys entirely. Read a missing `device` as UNKNOWN — never as `desktop`.
 */
export interface FunnelContext extends DeviceContext {
  /** The in-app browser we act on, or `false` for an ordinary one. */
  inApp: 'instagram' | 'telegram' | false;
  /** The OS under that in-app browser; `null` when `inApp` is false. */
  inAppOs: InAppOs | null;
}

/**
 * Computed ONCE per page load, not per event.
 *
 * The viewport and the user agent do not change under a single document (a
 * rotation can move `innerWidth` across a breakpoint, which is exactly the kind
 * of wobble that makes one visitor look like two devices in a group-by), and
 * `matchMedia` + a UA regex per event is work on a path that must never be
 * noticeable. The events that already stamp their OWN `deviceContext()` at click
 * time keep doing so and win the merge — see `track`.
 */
let cached: FunnelContext | null = null;

export function funnelContext(): FunnelContext {
  if (cached) return cached;
  const ctx: FunnelContext = {
    ...deviceContext(),
    // The two keys `inAppContext()` deliberately omits for an ordinary browser.
    inApp: false,
    inAppOs: null,
  };
  // Layered ON TOP with the SAME helper the checkout events already spread, so
  // an in-app row written here cannot disagree with one written there. It
  // returns `{}` for an ordinary browser, which is precisely the case the two
  // defaults above answer. (`Object.assign`, not a spread: a spread of its
  // union type is a duplicate-key compile error, TS2783.)
  Object.assign(ctx, inAppContext(detectInAppBrowser()));
  // Under SSR/prerender there is no window to describe, so nothing is cached
  // either: `deviceContext()` returns its desktop fallback there and caching it
  // would pin that guess onto the real browser's first event after hydration.
  if (typeof window !== 'undefined') cached = ctx;
  return ctx;
}

/** Test seam — forget the page context so the next `track()` recomputes it. */
export function resetFunnelContext(): void {
  cached = null;
}

export function track(event: string, meta?: Record<string, unknown>): void {
  // The page context first, the event's own meta second: an event that already
  // stamps `device`/`inApp` at the moment of a click (checkout_opened,
  // pay_once_opened, the hand-off pair) keeps ITS value, so no existing series
  // moves and nothing is written twice.
  const payload: Record<string, unknown> = { ...funnelContext(), ...meta };
  // Mirror the milestone to PostHog (custom event) so its funnels/replay can
  // filter on the same steps as the owned DIY funnel. No-op when PostHog is
  // dormant (no VITE_POSTHOG_KEY).
  phCapture(event, payload);
  if (typeof fetch === 'undefined') return;
  try {
    void fetch(COLLECT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonId: getOrCreateVisitorId(), event, meta: payload }),
      keepalive: true,
      credentials: 'include',
    }).catch(() => undefined);
  } catch {
    // analytics must never break the app
  }
}

/**
 * Fire a milestone at most once per browser session (sessionStorage guard) —
 * for events like `session_start` that must not repeat on every SPA navigation.
 */
export function trackOnce(event: string, meta?: Record<string, unknown>): void {
  try {
    const key = `fnl:${event}`;
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    }
  } catch {
    // private mode / no storage → just fire it
  }
  track(event, meta);
}
